import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { IJournalEntry, IPage, JournalSource } from '../../interfaces/accounting.interface';
import { formatMoney } from './money';
import { useAccountingInstitution } from './useAccountingInstitution';

const SOURCES: JournalSource[] = [
    'MANUAL', 'MONEY_IN', 'MONEY_OUT', 'TRANSFER', 'OPENING_BALANCE', 'PAYROLL', 'SALARY_PAYMENT', 'REVERSAL',
    'YEAR_END_CLOSE',
];

/** All journal entries, newest first, filterable by date range and source. */
const JournalEntryList: React.FC = () => {
    const institutionId = useAccountingInstitution();
    const navigate = useNavigate();
    const { t } = useTranslation('accounting');
    const { showApiError } = useToast();
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [source, setSource] = useState<JournalSource | ''>('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [data, setData] = useState<IPage<IJournalEntry> | null>(null);

    useEffect(() => {
        if (!institutionId) return;
        AccountingService.listEntries(institutionId, { from, to, source, page, size })
            .then(setData)
            .catch(error => showApiError(error, t('journalEntryList.loadFailed')));
    }, [institutionId, from, to, source, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>{t('journalEntryList.title')}</Typography>
                <Button variant="contained" component={RouterLink} to="/accounting/entries/new">{t('journalEntryList.newEntry')}</Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField id="entries-from" size="small" type="date" label={t('fields.from')} value={from}
                           onChange={e => { setFrom(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
                <TextField id="entries-to" size="small" type="date" label={t('fields.to')} value={to}
                           onChange={e => { setTo(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
                <TextField id="entries-source" size="small" select label={t('fields.source')} value={source}
                           onChange={e => { setSource(e.target.value as JournalSource | ''); setPage(0); }}
                           SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }}>
                    <option value="">{t('journalEntryList.allSources')}</option>
                    {SOURCES.map(s => <option key={s} value={s}>{t(`sources.${s}`)}</option>)}
                </TextField>
            </Stack>
            <Paper variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('fields.number')}</TableCell>
                            <TableCell>{t('fields.date')}</TableCell>
                            <TableCell>{t('fields.description')}</TableCell>
                            <TableCell>{t('fields.source')}</TableCell>
                            <TableCell align="right">{t('fields.amount')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.content.length === 0 && (
                            <TableRow><TableCell colSpan={5}>{t('journalEntryList.empty')}</TableCell></TableRow>
                        )}
                        {data?.content.map(e => (
                            <TableRow key={e.id} hover sx={{ cursor: 'pointer' }}
                                      onClick={() => navigate(`/accounting/entries/${e.id}`)}>
                                <TableCell>{e.entryNumber}</TableCell>
                                <TableCell>{e.entryDate}</TableCell>
                                <TableCell>
                                    {e.description}
                                    {e.status === 'REVERSED' && <Chip size="small" color="warning" label={t('chips.reversed')} sx={{ ml: 1 }} />}
                                </TableCell>
                                <TableCell>{SOURCES.includes(e.source) ? t(`sources.${e.source}`) : e.source}</TableCell>
                                <TableCell align="right">{formatMoney(e.total)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination component="div" count={data?.totalElements ?? 0} page={page} rowsPerPage={size}
                                 rowsPerPageOptions={[25, 50, 100]} onPageChange={(_, p) => setPage(p)}
                                 onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }} />
            </Paper>
        </Box>
    );
};

export default JournalEntryList;
